import React, { useState } from 'react';
import api from '../services/api';
import LoadingSpinner from '../components/LoadingSpinner';
import '../App.css';

interface Profile {
  id: string;
  firstName: string;
  lastName: string;
  email: string;
  profileType: string;
  createdAt: string;
}

interface DuplicateGroup {
  name: string;
  profiles: Profile[];
}

const AdminMergeAccounts: React.FC = () => {
  const [duplicates, setDuplicates] = useState<DuplicateGroup[]>([]);
  const [loading, setLoading] = useState(false);
  const [searched, setSearched] = useState(false);
  const [selectedPrimary, setSelectedPrimary] = useState<Record<string, string>>({});
  const [merging, setMerging] = useState(false);
  const [mergeResult, setMergeResult] = useState<string | null>(null);

  // Manual merge state
  const [manualPrimaryEmail, setManualPrimaryEmail] = useState('');
  const [manualSecondaryEmail, setManualSecondaryEmail] = useState('');

  const findDuplicates = async () => {
    setLoading(true);
    setSearched(true);
    try {
      const response = await api.get('/admin/duplicate-candidates');
      setDuplicates(response.data.duplicates || []);
    } catch (err: any) {
      alert(err.response?.data?.error || 'Failed to find duplicates');
    } finally {
      setLoading(false);
    }
  };

  const handleMerge = async (groupName: string, profiles: Profile[]) => {
    const primaryId = selectedPrimary[groupName];
    if (!primaryId) {
      alert('Select a primary account first');
      return;
    }

    const secondaryProfiles = profiles.filter((p) => p.id !== primaryId);
    if (secondaryProfiles.length === 0) return;

    const secondaryId = secondaryProfiles[0].id;
    const primaryProfile = profiles.find((p) => p.id === primaryId);

    if (!window.confirm(
      `Merge ${secondaryProfiles[0].email} INTO ${primaryProfile?.email}?\n\nAll data will be transferred to the primary account. The secondary account will be deleted. This cannot be undone.`
    )) return;

    setMerging(true);
    try {
      const response = await api.post('/admin/merge-accounts', { primaryId, secondaryId });
      setMergeResult(response.data.message);
      // Remove merged group from the list
      setDuplicates(duplicates.filter((d) => d.name !== groupName));
    } catch (err: any) {
      alert(err.response?.data?.error || 'Failed to merge accounts');
    } finally {
      setMerging(false);
    }
  };

  const handleManualMerge = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!window.confirm(`Merge ${manualSecondaryEmail} INTO ${manualPrimaryEmail}? This cannot be undone.`)) return;

    setMerging(true);
    try {
      // Look up users by email
      const [primaryRes, secondaryRes] = await Promise.all([
        api.get('/players', { params: { search: manualPrimaryEmail, limit: '1' } }),
        api.get('/players', { params: { search: manualSecondaryEmail, limit: '1' } }),
      ]);

      const primaryPlayers = primaryRes.data.players || primaryRes.data || [];
      const secondaryPlayers = secondaryRes.data.players || secondaryRes.data || [];

      const primary = primaryPlayers.find((p: any) => p.email === manualPrimaryEmail);
      const secondary = secondaryPlayers.find((p: any) => p.email === manualSecondaryEmail);

      if (!primary) {
        alert('Primary account not found');
        setMerging(false);
        return;
      }
      if (!secondary) {
        alert('Secondary account not found');
        setMerging(false);
        return;
      }

      const response = await api.post('/admin/merge-accounts', {
        primaryId: primary.id,
        secondaryId: secondary.id,
      });
      setMergeResult(response.data.message);
      setManualPrimaryEmail('');
      setManualSecondaryEmail('');
    } catch (err: any) {
      alert(err.response?.data?.error || 'Failed to merge accounts');
    } finally {
      setMerging(false);
    }
  };

  return (
    <div className="container">
      <h1>Merge Duplicate Accounts</h1>

      {mergeResult && (
        <div style={{ background: '#E8F5E9', padding: '15px', borderRadius: '8px', marginBottom: '20px', color: '#2E7D32' }}>
          {mergeResult}
          <button
            style={{ marginLeft: '15px', background: 'none', border: 'none', cursor: 'pointer', color: '#757575' }}
            onClick={() => setMergeResult(null)}
          >
            Dismiss
          </button>
        </div>
      )}

      {/* Auto-detect duplicates */}
      <div className="card" style={{ marginBottom: '20px' }}>
        <h2>Auto-Detect Duplicates</h2>
        <p style={{ color: '#757575', marginBottom: '15px' }}>
          Find accounts with matching first and last names.
        </p>
        <button className="btn btn-primary" onClick={findDuplicates} disabled={loading}>
          {loading ? 'Searching...' : 'Find Duplicates'}
        </button>
      </div>

      {searched && !loading && duplicates.length === 0 && (
        <div className="card" style={{ marginBottom: '20px' }}>
          <p>No duplicate accounts found.</p>
        </div>
      )}

      {duplicates.map((group) => (
        <div className="card" key={group.name} style={{ marginBottom: '15px' }}>
          <h3 style={{ marginBottom: '10px' }}>
            {group.profiles[0]?.firstName} {group.profiles[0]?.lastName}
            <span style={{ color: '#757575', fontSize: '14px', marginLeft: '10px' }}>
              ({group.profiles.length} accounts)
            </span>
          </h3>
          <div className="table-wrapper">
          <table className="table">
            <thead>
              <tr>
                <th>Primary</th>
                <th>Email</th>
                <th>Type</th>
                <th>Created</th>
                <th>ID</th>
              </tr>
            </thead>
            <tbody>
              {group.profiles.map((p) => (
                <tr key={p.id}>
                  <td>
                    <input
                      type="radio"
                      name={`primary-${group.name}`}
                      checked={selectedPrimary[group.name] === p.id}
                      onChange={() => setSelectedPrimary({ ...selectedPrimary, [group.name]: p.id })}
                    />
                  </td>
                  <td>{p.email}</td>
                  <td><span className="badge badge-info">{p.profileType}</span></td>
                  <td>{new Date(p.createdAt).toLocaleDateString()}</td>
                  <td style={{ fontFamily: 'monospace', fontSize: '12px' }}>{p.id.substring(0, 8)}...</td>
                </tr>
              ))}
            </tbody>
          </table>
          </div>
          <button
            className="btn btn-danger"
            style={{ marginTop: '10px' }}
            onClick={() => handleMerge(group.name, group.profiles)}
            disabled={merging || !selectedPrimary[group.name]}
          >
            {merging ? 'Merging...' : 'Merge (keep selected primary)'}
          </button>
        </div>
      ))}

      {/* Manual merge */}
      <div className="card" style={{ marginTop: '20px' }}>
        <h2>Manual Merge</h2>
        <p style={{ color: '#757575', marginBottom: '15px' }}>
          Merge two specific accounts by email. All data from the secondary account will be transferred to the primary.
        </p>
        <form onSubmit={handleManualMerge}>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '15px' }}>
            <div className="form-group">
              <label>Primary Account (keep)</label>
              <input
                type="email"
                value={manualPrimaryEmail}
                onChange={(e) => setManualPrimaryEmail(e.target.value)}
                placeholder="primary@example.com"
                required
              />
            </div>
            <div className="form-group">
              <label>Secondary Account (merge & delete)</label>
              <input
                type="email"
                value={manualSecondaryEmail}
                onChange={(e) => setManualSecondaryEmail(e.target.value)}
                placeholder="duplicate@example.com"
                required
              />
            </div>
          </div>
          <button type="submit" className="btn btn-danger" disabled={merging}>
            {merging ? 'Merging...' : 'Merge Accounts'}
          </button>
        </form>
      </div>
    </div>
  );
};

export default AdminMergeAccounts;
